import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelSessionModal } from '@/components/ui/CancelSessionModal';
import { SessionModal } from '@/components/ui/SessionModal';
import { useCancelProfessionalSession } from '@/services';
import { buildProfessionalSessionEditUrlEncoded } from '@/pages/professional/sessions/utils/professionalSessionEditNavigation';
import { professionalHasP2PPermission } from '@/utils/professionalPermissionTags';
import { getProfessionalData } from '@/utils/cookies';
import {
	canProfessionalCancelSession,
	canProfessionalManageP2PSession,
} from '@/utils/sessionScheduledBy';
import { getSessionPatientDisplayName } from '../utils/getSessionPatientDisplayName';
import { SessionDetailModal } from './SessionDetailModal';

interface ProfessionalSessionModalProps {
	session: Sessions.Session | null;
	isOpen: boolean;
	onClose: () => void;
}

const ProfessionalSessionModal = ({
	session,
	isOpen,
	onClose,
}: ProfessionalSessionModalProps) => {
	const navigate = useNavigate();
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

	const { mutate: cancelSession, isPending: isCancellingSession } =
		useCancelProfessionalSession(() => {
			setIsDeleteConfirmOpen(false);
			onClose();
		});

	const canManageSession =
		session != null &&
		professionalHasP2PPermission(getProfessionalData()) &&
		canProfessionalManageP2PSession(session);
	const canCancelSession =
		session != null && canProfessionalCancelSession(session);

	const patientLabel = session ? getSessionPatientDisplayName(session) : '';

	const handleEditSession = () => {
		if (!session) return;

		const encodedParams = buildProfessionalSessionEditUrlEncoded(session);
		if (!encodedParams) return;

		onClose();
		navigate(`/profissional/consultas/agendar?d=${encodedParams}`);
	};

	const handleConfirmDelete = (reason: string) => {
		if (!session || isCancellingSession) return;
		cancelSession({ id: session.id, reason });
	};

	return (
		<>
			<SessionModal
				isOpen={isOpen}
				onClose={onClose}
				showEditAction={canManageSession}
				onEdit={handleEditSession}
				showDeleteAction={canCancelSession}
				onDelete={() => setIsDeleteConfirmOpen(true)}
			>
				{session && <SessionDetailModal session={session} />}
			</SessionModal>

			<CancelSessionModal
				isOpen={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				onConfirm={handleConfirmDelete}
				session={session}
				actor="professional"
				counterpartName={patientLabel || undefined}
				isPending={isCancellingSession}
			/>
		</>
	);
};

export { ProfessionalSessionModal };
